import { Button } from "@/components/ui/button"
import { useI18n } from "@/lib/i18n"
import { faBars, faXmark } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { clsx } from "clsx"
import { AnimatePresence, motion } from "framer-motion"
import React, { useEffect, useRef, useState } from "react"
import { Link, useLocation } from "react-router"
import { PorematLogo } from "./poremat-logo"

export const Header: React.FC = () => {
  const { locale, setLocale, t } = useI18n()
  const location = useLocation()
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const prevLocationRef = useRef(location.pathname)

  useEffect(() => {
    const handleScroll = (): void => {
      setIsScrolled(window.scrollY > 20)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    const shouldClose = prevLocationRef.current !== location.pathname
    prevLocationRef.current = location.pathname

    if (shouldClose) {
      // Use a microtask to avoid setState during render
      queueMicrotask(() => {
        setIsMenuOpen(false)
      })
    }
  }, [location.pathname])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return (): void => {
      document.body.style.overflow = ""
    }
  }, [isMenuOpen])

  const navLinks = [
    { label: t("nav.company"), path: "/company" },
    { label: t("nav.ourApproach"), path: "/our-approach" },
    { label: t("nav.products"), path: "/products" },
    { label: t("nav.resources"), path: "/resources" },
  ]

  const toggleLanguage = (): void => {
    setLocale(locale === "tr" ? "en" : "tr")
  }

  return (
    <header
      className={clsx(
        "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
        isScrolled
          ? "bg-white/90 backdrop-blur-md shadow-md"
          : "bg-white shadow-sm"
      )}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <PorematLogo className="h-8 lg:h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={clsx(
                  "text-sm font-medium transition-colors",
                  location.pathname === link.path
                    ? "text-[#0077B6]"
                    : "text-gray-700 hover:text-[#0077B6]"
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            {/* Language Switcher */}
            <Button
              onClick={toggleLanguage}
              variant="ghost"
              size="sm"
              aria-label="Switch language"
              className="cursor-pointer hover:bg-gray-100 transition-colors font-semibold px-4 py-2 rounded-md border border-gray-200 hover:border-[#0077B6]"
            >
              {locale.toUpperCase()}
            </Button>

            {/* Contact Button */}
            <Button
              asChild
              className="hidden sm:inline-flex gradient-brand-bg text-white hover:opacity-85"
            >
              <Link to="/contact">{t("nav.contact")}</Link>
            </Button>

            {/* Mobile Menu Toggle */}
            <Button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              variant="ghost"
              size="icon"
              className="lg:hidden"
              aria-label="Toggle menu"
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? (
                <FontAwesomeIcon icon={faXmark} className="w-6 h-6" />
              ) : (
                <FontAwesomeIcon icon={faBars} className="w-6 h-6" />
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMenuOpen && (
          <>
            {/* Menu Panel */}
            <motion.div
              initial={{ opacity: 0, x: "100%" }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="static right-0 top-16 bottom-0 w-full bg-white shadow-xl lg:hidden overflow-y-auto z-50"
            >
              <nav className="flex flex-col p-6 space-y-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="block py-3 text-lg font-medium text-gray-900 hover:text-[#0077B6]"
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  to="/contact"
                  className="block py-3 text-lg font-medium text-gray-900 hover:text-[#0077B6] sm:hidden"
                >
                  {t("nav.contact")}
                </Link>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  )
}
